import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  Renderer2,
  OnChanges,
  SimpleChanges,
  OnDestroy
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import confetti from 'canvas-confetti';

export interface UnlockedReward {
  id: string;
  name: string;
  description?: string;
  icon: string;
}

export interface UnlockedTierData {
  name: string;
  backgroundColor: string;
  rewards: UnlockedReward[];
  subtitle?: string; // Optional subtitle, defaults to "Check out your new rewards"
  earningInfo?: string; // For Red tier: "Plus you'll earn 1pt for every ₹100 spent..."
  additionalBenefits?: string; // For Black tier: "Plus invites to styling sessions..."
  showSubtitle?: boolean; // Control subtitle visibility, defaults to true
}

@Component({
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  selector: 'app-unlocked-popup-modal',
  templateUrl: './unlocked-popup-modal.component.html',
  styleUrl: './unlocked-popup-modal.component.scss'
})
export class UnlockedPopupModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() showModal!: boolean;
  @Input() tierData?: UnlockedTierData;
  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() exploreClick = new EventEmitter<void>();
  @Output() rewardClick = new EventEmitter<UnlockedReward>();

  private confettiAnimationFrame?: number;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    if (this.showModal) {
      this.renderer.addClass(this.document.body, 'no-scroll');
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['showModal']) {
      if (changes['showModal'].currentValue) {
        this.renderer.addClass(this.document.body, 'no-scroll');
        // Start confetti side cannons
        this.startConfettiSideCannons();
      } else {
        this.renderer.removeClass(this.document.body, 'no-scroll');
        // Stop confetti animation
        this.stopConfetti();
      }
    }
  }

  ngOnDestroy(): void {
    this.stopConfetti();
  }

  private startConfettiSideCannons(): void {
    const colors = this.getColorVariations();
    const end = Date.now() + 3 * 1000; // 3 seconds

    const frame = () => {
      if (Date.now() > end) {
        return;
      }

      // Left side cannon (angle 60 degrees, from x: 0, y: 0.5)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-floating-promises
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        startVelocity: 60,
        origin: { x: 0, y: 0.5 },
        colors: colors
      });

      // Right side cannon (angle 120 degrees, from x: 1, y: 0.5)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-floating-promises
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        startVelocity: 60,
        origin: { x: 1, y: 0.5 },
        colors: colors
      });

      this.confettiAnimationFrame = requestAnimationFrame(frame);
    };

    frame();
  }

  private stopConfetti(): void {
    if (this.confettiAnimationFrame) {
      cancelAnimationFrame(this.confettiAnimationFrame);
      this.confettiAnimationFrame = undefined;
    }
    // Reset confetti to clear any remaining particles
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    confetti.reset();
  }

  closePopup() {
    this.stopConfetti();
    this.exploreClick.emit();
    this.showModal = false;
    this.showModalChange.emit(this.showModal);
    this.renderer.removeClass(this.document.body, 'no-scroll');
  }

  getColorVariations(): string[] {
    if (!this.tierData?.backgroundColor) {
      return ['#0D0D0C', '#FFFFFF'];
    }

    const bgColor = this.tierData.backgroundColor.toUpperCase();
    if (bgColor.includes('D00E16') || bgColor.includes('RED')) {
      return ['#D00E16', '#FAB7BA'];
    } else if (bgColor.includes('003380') || bgColor.includes('BLUE')) {
      return ['#003380', '#5B9BD5'];
    } else if (bgColor.includes('0D0D0C') || bgColor.includes('BLACK')) {
      return ['#0D0D0C', '#FFFFFF'];
    }

    return ['#0D0D0C', '#FFFFFF'];
  }

  getTierIconBgColor(): string {
    if (!this.tierData?.backgroundColor) {
      return '#E0E0E0';
    }

    const bgColor = this.tierData.backgroundColor.toUpperCase();
    if (bgColor.includes('D00E16') || bgColor.includes('RED')) {
      return '#FAB7BA';
    } else if (bgColor.includes('003380') || bgColor.includes('BLUE')) {
      return '#D3E3F8';
    } else if (bgColor.includes('0D0D0C') || bgColor.includes('BLACK')) {
      return '#E0E0E0';
    }

    return '#E0E0E0';
  }

  getTierName(): string {
    if (!this.tierData?.name) return 'Black';
    return this.tierData.name.split('|')[0].trim();
  }

  getTierLogoPath(): string {
    if (!this.tierData?.name) {
      return 'assets/images/tier/blacktierlogo.svg';
    }
    const parts = this.tierData.name.split('|');
    const logoName =
      parts.length > 1
        ? parts[1].trim()
        : parts[0].toLowerCase().replace(/\s+/g, '') + 'tierlogo.svg';
    return `assets/images/tier/${logoName}`;
  }

  getSubtitle(): string {
    return this.tierData?.subtitle ?? 'Check out your new rewards';
  }

  shouldShowSubtitle(): boolean {
    return this.tierData?.showSubtitle !== false;
  }

  getRewards(): UnlockedReward[] {
    return this.tierData?.rewards || [];
  }
}
